import type { GeoUnitTypeKey } from './geoAnalytics'

export type GoalKind = 'mobilize' | 'persuade' | 'both'
export type IssueDirection = 'pro' | 'opposed' | 'skip'
export type IssueKey = 'choice' | 'gunControl' | 'climate'
export type OfficeKey = 'StateHouse' | 'StateSenate' | 'USHouse'

export interface GuidedIssueSelection {
  issue: IssueKey
  direction: Exclude<IssueDirection, 'skip'>
}

export interface GuidedListSpec {
  state: string
  office: OfficeKey
  districtCode?: string | null
  goal: GoalKind
  issues: GuidedIssueSelection[]
  ideologyBands: string[]
  votePropBands: string[]
  demPrimaryBands: string[]
  activismBands: string[]
  educationBands: string[]
  races: string[]
  genders: string[]
  generations: string[]
  ageBins: string[]
  urbanities: string[]
  incomeBins: string[]
  smartFlags: string[]
  registeredDemOnly: boolean
  registrationActiveOnly: boolean
  permanentAbsenteeOnly: boolean
  excludeCellDnc: boolean
  excludeLandDnc: boolean
  excludeDeceased: boolean
  excludeAlreadyContacted: boolean
  unionMemberOnly: boolean
}

export const GUIDED_SPEC_DEFAULTS: GuidedListSpec = {
  state: '', office: 'StateSenate', districtCode: null, goal: 'both', issues: [],
  ideologyBands: [], votePropBands: [], demPrimaryBands: [], activismBands: [], educationBands: [],
  races: [], genders: [], generations: [], ageBins: [], urbanities: [], incomeBins: [], smartFlags: [],
  registeredDemOnly: true, registrationActiveOnly: true, permanentAbsenteeOnly: false,
  excludeCellDnc: false, excludeLandDnc: false, excludeDeceased: true,
  excludeAlreadyContacted: false, unionMemberOnly: false,
}

/** Only the three issues with real person-level data (spec YAGNI). `pro` = the human-facing verb. */
export const GUIDED_ISSUES: { key: IssueKey; label: string; proLabel: string }[] = [
  { key: 'choice', label: 'Reproductive rights', proLabel: 'Pro-choice' },
  { key: 'gunControl', label: 'Gun-control', proLabel: 'Pro' },
  { key: 'climate', label: 'Climate action', proLabel: 'Pro' },
]

const OFFICE_UNIT_TYPE: Record<OfficeKey, GeoUnitTypeKey> = {
  StateHouse: 'stateHouseDistrict',
  StateSenate: 'stateSenateDistrict',
  USHouse: 'congressionalDistrict',
}
export function officeUnitType(office: OfficeKey): GeoUnitTypeKey {
  return OFFICE_UNIT_TYPE[office]
}

/** Human-facing office names (keyed in selector display order). Never render the raw enum key. */
export const OFFICE_LABELS: Record<OfficeKey, string> = {
  StateHouse: 'State House',
  StateSenate: 'State Senate',
  USHouse: 'US House',
}
export const OFFICE_ORDER: OfficeKey[] = ['StateHouse', 'StateSenate', 'USHouse']
export function officeLabel(office: OfficeKey): string {
  return OFFICE_LABELS[office] ?? office
}

/** District code = trailing integer of the unit NAME (not profile.code, which is FIPS-prefixed). */
export function parseDistrictCode(unitName: string): string | null {
  return unitName.match(/(\d+)\s*$/)?.[1] ?? null;
}

/** Compliance abstraction: describe the filter with OUR words — never a Catalist enum value. */
export function issueCompileCaption(goal: GoalKind, direction: IssueDirection, issueLabel: string): string {
  if (direction === 'opposed') return `→ opposed to ${issueLabel}`
  const strength = goal === 'mobilize' ? 'committed' : goal === 'persuade' ? 'persuadable' : 'likely'
  return `→ ${strength} supporters of ${issueLabel}`
}

export interface BandOption { key: string; label: string }

export const SCORE_BANDS = {
  ideology: [
    { key: 'progressive', label: 'Progressive' },
    { key: 'moderate', label: 'Moderate' },
    { key: 'conservative', label: 'Conservative' },
  ] as BandOption[],
  lmh: [
    { key: 'low', label: 'Low' },
    { key: 'medium', label: 'Medium' },
    { key: 'high', label: 'High' },
  ] as BandOption[],
}

/** True when the selected band keys skip a gap in the ordered band list (e.g. Low+High). */
export function isNonAdjacent(selected: string[], order: string[]): boolean {
  const idx = selected.map(k => order.indexOf(k)).filter(i => i >= 0).sort((a, b) => a - b)
  if (idx.length < 2) return false
  return idx[idx.length - 1] - idx[0] + 1 !== idx.length
}

/** Coalition/demographic knobs: spec key → { field label, options, value }. Values mirror the
 *  backend registry enum values verbatim. Model-score tooltips live on the band rows. */
export const AUDIENCE_DEMOGRAPHICS = [
  { specKey: 'races', label: 'Race / Ethnicity', options: ['asian', 'black', 'caucasian', 'hispanic', 'nativeAmerican', 'unknown'] },
  { specKey: 'genders', label: 'Gender', options: ['M', 'F', 'O'] },
  { specKey: 'generations', label: 'Generation', options: ['Gen Z', 'Millennial', 'Gen X', 'Boomer', 'Silent'] },
  { specKey: 'ageBins', label: 'Age band', options: ['Under 25', '25 to 39', '40 to 49', '50 to 64', '65 +'] },
  { specKey: 'urbanities', label: 'Urbanity', options: ['Rural', 'Urban', 'Suburban'] },
  { specKey: 'incomeBins', label: 'Income', options: ['Less than $20,000', '$20,000 - $30,000', '$30,000 - $50,000', '$50,000 - $75,000', '$75,000 - $100,000', '$100,000 - $150,000', 'Greater than $150,000'] },
] as const

export const SCORE_KNOBS = [
  { specKey: 'ideologyBands', label: 'Ideology', bands: 'ideology', tip: 'How progressive or conservative someone leans overall, from a Catalist model.' },
  { specKey: 'votePropBands', label: 'General vote propensity', bands: 'lmh', tip: 'How likely someone is to vote in a typical general election.' },
  { specKey: 'demPrimaryBands', label: 'Dem-primary propensity', bands: 'lmh', tip: 'How likely someone is to vote in a Democratic primary.' },
  { specKey: 'activismBands', label: 'Activism', bands: 'lmh', tip: 'How civically engaged someone is — donating, volunteering, contacting officials, signing petitions.' },
  { specKey: 'educationBands', label: 'Education', bands: 'lmh', tip: 'Modeled education level.' },
] as const
